import { Empty, Modal, Skeleton, Spin, Timeline, Typography } from "antd";
import dayjs from "dayjs";
import { isEmpty } from "lodash-es";
import { useAuditLog } from "queries/auditLog";
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";
import { FaDotCircle } from "react-icons/fa";

const { Text } = Typography;

function formatScheduleExtra(extra) {
  if (!extra) return [];

  const date = extra["Date"];
  const startTime = extra["Start Time"];
  const endTime = extra["End Time"];

  const contactRaw = extra["Contact"] ?? "";
  const contactName = contactRaw.split(",")[1]?.trim() || contactRaw.trim() || null;

  const rows = [];

  if (date && startTime && endTime) {
    const start = dayjs(`${date} ${startTime}`).format("DD MMM YYYY HH:mm");
    const end = dayjs(`${date} ${endTime}`).format("HH:mm");
    rows.push({ label: "Date", value: `${start} - ${end}` });
  }

  if (extra["Note"]) rows.push({ label: "Note", value: extra["Note"] });
  if (extra["Schedule Admin Name"]) rows.push({ label: "Admin", value: extra["Schedule Admin Name"] });
  if (extra["Creator Admin"]) rows.push({ label: "Creator", value: extra["Creator Admin"] });
  if (contactName) rows.push({ label: "Contact", value: contactName });
  if (extra["Status Name"]) rows.push({ label: "Status", value: extra["Status Name"] });

  return rows;
}

const ExtraTable = ({ extra }) => {
  const rows = useMemo(() => formatScheduleExtra(extra), [extra]);
  if (!rows.length) return null;

  return (
    <table className="w-full border-collapse mt-1 mb-1" style={{ fontSize: 11 }}>
      <tbody>
        {rows.map(({ label, value }) => (
          <tr key={label} className="border-b border-neutral-100 last:border-0">
            <td className="py-0.5 pr-3 text-neutral-400 whitespace-nowrap align-top w-20">
              {label}
            </td>
            <td className="py-0.5 text-neutral-700 break-words">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const ScheduleChangelogModal = ({ open, onClose, scheduleId }) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);

  const { data, fetchNextPage, isFetchingNextPage, isLoading } = useAuditLog(
    { payload: { tran_id: scheduleId, trans_type_id: 84 } },
    { enabled: open && Boolean(scheduleId) }
  );

  const formattedData = useMemo(() => {
    if (!data) return { data: [], total: 0 };
    const rows = [];
    let total = 0;
    (data.pages || []).forEach((page) => {
      total = page?.total ?? total;
      (page?.data || []).forEach((row) => rows.push(row));
    });
    return { data: rows, total };
  }, [data]);

  const iconColor = useCallback((action) => {
    switch (action) {
      case "add": return "green-400";
      case "delete": return "red-400";
      default: return "primary";
    }
  }, []);

  const timelineItems = useMemo(() => {
    if (isEmpty(formattedData.data)) return [];
    return formattedData.data.map((item) => ({
      dot: (
        <FaDotCircle
          className={`text-sm relative top-1 text-${iconColor(item?.event_category)}`}
        />
      ),
      children: (
        <div className="flex flex-col pt-[2px]">
          <div className="flex items-baseline gap-2 mb-1">
            <span className="text-[13px]">
              {item?.desc?.action && (
                <>
                  {item.desc.action}
                  {item?.desc?.name && ` ${t("audit.by")} ${item.desc.name}`}
                </>
              )}
            </span>
            <Text className="text-[10px] text-neutral-400 shrink-0">
              {dayjs.tz(item?.created_at).from(dayjs().tz())}
            </Text>
          </div>
          <ExtraTable extra={item?.extra} />
          <Text className="text-[10px] text-neutral-400 mt-1">
            {dayjs(item.created_at).format("DD MMM YYYY HH:mm")}
          </Text>
        </div>
      ),
    }));
  }, [formattedData.data, iconColor, t]);

  return (
    <Modal
      title={t("meeting_management.change_log")}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnHidden
      width={520}
    >
      <Skeleton loading={isLoading} active>
        <div
          ref={scrollRef}
          style={{ height: "65vh", overflowY: "auto", overflowX: "hidden", paddingTop: 8, paddingLeft: 8 }}
        >
          <InfiniteScroll
            initialLoad={false}
            pageStart={1}
            loadMore={fetchNextPage}
            hasMore={formattedData.data.length < formattedData.total}
            useWindow={false}
            getScrollParent={() => scrollRef.current}
          >
            {isEmpty(formattedData.data) ? (
              <Empty className="my-10" />
            ) : (
              <Timeline
                items={timelineItems}
                pending={isFetchingNextPage}
                pendingDot={<div className="mt-5"><Spin /></div>}
              />
            )}
          </InfiniteScroll>
        </div>
      </Skeleton>
    </Modal>
  );
};

export default ScheduleChangelogModal;
